import React, { PureComponent } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { Field, reduxForm, formValueSelector } from "redux-form";
import { onlyNumbers } from "utils/normalize";
import {
  required,
  isPhoneNumEqual10,
  onlyNumberAndLetters,
} from "utils/validations";
import { TextField, SelectField } from "components/Fields";
import { ID_STATE } from "containers/MultistepForm/constants/collections";

const FORM_NAME = "fypn_additional_info_form";
const selector = formValueSelector(FORM_NAME);

const FIELDS = [
  "licenseNumber",
  "licenseState",
  "employer",
  "employerCity",
  "employerState",
  "employerZip",
  "workPhone",
  "yearsAtEmployer",
  "monthsAtEmployer",
  "bankName",
];

const MONTHS_AT_EMPLOYER = Array.from({ length: 12 }, (_, i) => ({
  label: `${i}`,
  value: `${i}`,
}));

const isZip = (value) =>
  value && !/^\d{5}$/.test(value) ? "ZIP must be 5 digits" : undefined;

const isYears = (value) =>
  value && !/^\d{1,2}$/.test(value) ? "Enter a number of years" : undefined;

class Form extends PureComponent {
  componentDidMount() {
    this.handleChange();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.additionalData !== this.props.additionalData) {
      this.handleChange();
    }
  }

  handleChange = () => {
    const { additionalData, onDataChange, valid } = this.props || {};
    const filled = FIELDS.every(
      (field) => additionalData[field] !== undefined && additionalData[field] !== ""
    );
    onDataChange({
      data: additionalData,
      valid: !!valid && filled,
    });
  };

  renderField = ({ input, meta, label, placeholder, inputMode }) => (
    <TextField
      type="text"
      label={label}
      placeholder={placeholder}
      meta={meta}
      inputMode={inputMode}
      customChange={this.handleChange}
      {...input}
    />
  );

  renderSelectField = ({ input, meta, label, placeholder, options }) => (
    <SelectField
      skipBlur
      label={label}
      meta={meta}
      {...input}
      options={options}
      placeholder={placeholder}
      customChange={this.handleChange}
    />
  );

  renderSectionHeader = (title, icon) => (
    <div className="fypn-additional-info-form__section-header">
      {icon}
      <span className="fypn-additional-info-form__section-title">{title}</span>
    </div>
  );

  render() {
    return (
      <div className="fypn-additional-info-form__form">
        <div className="fypn-additional-info-form__section">
          {this.renderSectionHeader(
            "Identification Info",
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2">
              <rect x="2" y="4" width="20" height="16" rx="2" />
              <circle cx="8.5" cy="11" r="2.5" />
              <path d="M4 18c0-2.2 2-4 4.5-4S13 15.8 13 18M15 9h5M15 13h5" />
            </svg>
          )}

          <div className="fypn-additional-info-form__field-row">
            <div className="fypn-additional-info-form__field">
              <Field
                name="licenseNumber"
                label="ID / License Number *"
                placeholder="License number"
                validate={[required, onlyNumberAndLetters]}
                component={this.renderField}
              />
            </div>
            <div className="fypn-additional-info-form__field">
              <Field
                name="licenseState"
                label="ID State *"
                placeholder="State"
                options={ID_STATE}
                validate={[required]}
                component={this.renderSelectField}
              />
            </div>
          </div>
        </div>

        <div className="fypn-additional-info-form__section">
          {this.renderSectionHeader(
            "Employment Info",
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2">
              <rect x="2" y="7" width="20" height="14" rx="2" />
              <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2M2 13h20" />
            </svg>
          )}

          <div className="fypn-additional-info-form__field">
            <Field
              name="employer"
              label="Employer *"
              placeholder="Employer name"
              validate={[required]}
              component={this.renderField}
            />
          </div>

          <div className="fypn-additional-info-form__field">
            <Field
              name="employerCity"
              label="City *"
              placeholder="City"
              validate={[required]}
              component={this.renderField}
            />
          </div>

          <div className="fypn-additional-info-form__field-row">
            <div className="fypn-additional-info-form__field">
              <Field
                name="employerState"
                label="State *"
                placeholder="State"
                options={ID_STATE}
                validate={[required]}
                component={this.renderSelectField}
              />
            </div>
            <div className="fypn-additional-info-form__field">
              <Field
                name="employerZip"
                label="ZIP *"
                placeholder="ZIP"
                inputMode="numeric"
                normalize={onlyNumbers}
                validate={[required, isZip]}
                component={this.renderField}
              />
            </div>
          </div>

          <div className="fypn-additional-info-form__field">
            <Field
              name="workPhone"
              label="Employer Phone *"
              placeholder="10-digit phone number"
              inputMode="tel"
              normalize={onlyNumbers}
              validate={[required, isPhoneNumEqual10]}
              component={this.renderField}
            />
          </div>

          <div className="fypn-additional-info-form__subsection-title">
            How long have you worked here?
          </div>
          <div className="fypn-additional-info-form__field-row">
            <div className="fypn-additional-info-form__field">
              <Field
                name="yearsAtEmployer"
                label="Years *"
                placeholder="Years"
                inputMode="numeric"
                normalize={onlyNumbers}
                validate={[required, isYears]}
                component={this.renderField}
              />
            </div>
            <div className="fypn-additional-info-form__field">
              <Field
                name="monthsAtEmployer"
                label="Months *"
                placeholder="Months"
                options={MONTHS_AT_EMPLOYER}
                validate={[required]}
                component={this.renderSelectField}
              />
            </div>
          </div>
        </div>

        <div className="fypn-additional-info-form__section">
          {this.renderSectionHeader(
            "Banking Info",
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2">
              <path d="M3 21h18M3 10h18M5 6l7-3 7 3M4 10v11M20 10v11M8 14v3M12 14v3M16 14v3" />
            </svg>
          )}

          <div className="fypn-additional-info-form__field">
            <Field
              name="bankName"
              label="Bank Name *"
              placeholder="Bank name"
              validate={[required]}
              component={this.renderField}
            />
          </div>
        </div>
      </div>
    );
  }
}

Form.propTypes = {
  onDataChange: PropTypes.func,
};

Form.defaultProps = {
  onDataChange: () => {},
};

Form = reduxForm({
  form: FORM_NAME,
  destroyOnUnmount: false,
  forceUnregisterOnUnmount: true,
})(Form);

Form = connect((state) => ({
  additionalData: selector(state, ...FIELDS),
}))(Form);

export default Form;
